import React, { useMemo } from 'react';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { queryClient, router } from '../../../../App';
import { getStore } from '../../../../store/store';
import {
  useAorSitrep,
  useSitrepV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { sitrepSectionDisplay } from 'components/Reports/document/sitrepSectionDisplay';
import { fullSitrepHref, textareaSections } from './aorPopupSections';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import {
  getEpaFemaAorSitrep,
  type EpaFemaAorSitrep,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/epaFemaAorSitreps';
import { useEpaFlag } from 'utils/featureFlags';

type AorPopupContentProps = {
  aorId: string;
  description: string;
};

function SeededEpaSitrepCard({ sitrep }: { sitrep: EpaFemaAorSitrep }) {
  return (
    <div className="tw:space-y-1.5 tw:rounded-md tw:border tw:border-border tw:bg-card tw:p-2.5 tw:text-xs">
      <p className="tw:m-0 tw:text-sm tw:font-semibold">{sitrep.title}</p>
      <p className="tw:m-0 tw:text-muted-foreground">
        Approved by: {sitrep.approvedBy}
      </p>
      <p className="tw:m-0 tw:text-muted-foreground">{sitrep.timestamp}</p>
      <p className="tw:m-0 tw:leading-snug">{sitrep.situation}</p>
    </div>
  );
}

const AorPopupSitrepPreview: React.FC<{
  sitrepId: string;
  aorId: string;
}> = ({ sitrepId, aorId }) => {
  const { data: sitrep, isLoading } = useSitrepV2(sitrepId);
  const sections = useMemo(
    () => textareaSections(sitrep?.sections),
    [sitrep?.sections]
  );

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <Button
        type="button"
        size="sm"
        className="tw:w-full"
        onClick={() => {
          void router.navigate(fullSitrepHref(sitrepId, aorId));
        }}
      >
        Open Full SITREP
      </Button>
      {isLoading ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Loading report…
        </p>
      ) : (
        sections.map((section) => {
          const text =
            sitrepSectionDisplay(section).display?.sitrep_section_text || '';
          return (
            <div key={section.id}>
              <div className="tw:mb-1 tw:text-sm tw:font-semibold">
                {section.name}
              </div>
              <TiptapRichTextEditor value={text} readOnly showToolbar={false} />
            </div>
          );
        })
      )}
    </div>
  );
};

const AorPopupContentInner: React.FC<AorPopupContentProps> = ({
  aorId,
  description,
}) => {
  const epaEnabled = useEpaFlag();
  const { data: aorSitrep } = useAorSitrep(aorId);
  const seededSitrep = epaEnabled ? getEpaFemaAorSitrep(aorId) : null;

  return (
    <div style={{ maxWidth: '400px' }}>
      {description && (
        <div className="mb-3">
          <TiptapRichTextEditor
            value={description}
            readOnly
            showToolbar={false}
          />
        </div>
      )}
      {aorSitrep ? (
        <AorPopupSitrepPreview sitrepId={aorSitrep.id} aorId={aorId} />
      ) : seededSitrep ? (
        <SeededEpaSitrepCard sitrep={seededSitrep} />
      ) : null}
    </div>
  );
};

const AorPopupContent: React.FC<AorPopupContentProps> = (props) => {
  const store = getStore();

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AorPopupContentInner {...props} />
      </QueryClientProvider>
    </Provider>
  );
};

export default AorPopupContent;
